import { useEffect, useState } from 'react';
import { postQuoteApproval, WorkflowOrder } from '../../../Api/Orders/ordersApi';
import { DetailViewCard } from '../../Compounds/CardWithTitle';
import { DetailRow } from '../../Compounds/DetailsDrawer/DetailRow';
import { getWorkflowOrdersLoadingSubject } from '../../../store/WorkflowOrder';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { AttachmentTable } from '../../Compounds/AttachmentTable';
import { DetailsComments } from '../../Compounds/DetailsComments/DetailsComments';
import { shouldSeeComments } from '../../../store/User';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { QuoteApprovalSection } from './QuoteApprovalSection';
import { orderRequestDetailFields } from './orderRequestDetailFields';
import { orderFilesParentId, orderThreadId } from './orderKeys';

export interface WorkflowOrderDetailsProps {
  data: WorkflowOrder;
  approvalEnabled: boolean;
  onApprovalComplete?: () => void;
}

export function WorkflowOrderDetails({ data, approvalEnabled, onApprovalComplete }: WorkflowOrderDetailsProps) {
  const [loading, setLoading] = useState(false);
  const { setSnack } = useSnackbar();

  useEffect(() => {
    const sub = getWorkflowOrdersLoadingSubject().subscribe(setLoading);
    return () => sub.unsubscribe();
  }, []);

  const showApproval =
    approvalEnabled && data.workflow_tab === 'reviewing_approving_quotes' && data.source_type === 'opportunity';
  const threadId = orderThreadId(data);

  const handleApprove = async (quoteIds: string[]) => {
    const { error } = await postQuoteApproval(data.sf_id, quoteIds, data.name || undefined, data.account_id);
    if (error) {
      setSnack({ message: error.message || 'Failed to approve quotes', type: 'error', open: true });
      return false;
    }
    setSnack({ message: 'Quotes approved successfully', type: 'success', open: true });
    onApprovalComplete?.();
    return true;
  };

  const fields = orderRequestDetailFields(data);

  return (
    <>
      <DetailViewCard
        collapsed={false}
        enableCollapse
        key="order-request-details"
        title="Order Request Details">
        <div className="overflow-auto max-h-[30vh]">
          {loading && (
            <div className="mt-32">
              <ComponentSpinner />
            </div>
          )}
          {!loading &&
            fields.map((field) => (
              <DetailRow
                key={field.title}
                colId={field.title}
                title={field.title}
                value={field.value || '—'}
              />
            ))}
        </div>
      </DetailViewCard>
      {showApproval && (
        <QuoteApprovalSection
          opportunitySfId={data.sf_id}
          accountId={data.account_id}
          onApprove={handleApprove}
        />
      )}
      {data.sf_id && (
        <AttachmentTable
          id={orderFilesParentId(data)}
          parentType="Opportunity"
          readOnly
        />
      )}
      {shouldSeeComments() && threadId && (
        <DetailsComments
          id={threadId}
          entityType="Opportunity"
          entityAccountId={data.account_id || ''}
        />
      )}
    </>
  );
}
